// src/modules/app-views/products/pages/Document/modals/StockMovementModal/index.jsx
// *******************************************************************************
// STOCK MOVEMENT MODAL
// *******************************************************************************
// GenericModal for creating and editing manual stock movements.
// Edit mode: isLastMovement controls which fields are editable.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo } from 'react';
import { SwapOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import translateX from 'utils/translateX';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import { API } from 'constants/Api';
import step1fn from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const StockMovementModal = ({
  show,
  setShow,
  productId,
  stockQuantity = 0,
  allowNegativeStock = false,
  editingMovement = null,
  isLastMovement = false,
  onSuccess,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEdit = !!editingMovement;

  const defaultWarehouseUid = useMemo(() => {
    const raw = appService.getModulePreferences({
      moduleId: 'products',
      category: 'warehouses',
    });
    if (!Array.isArray(raw)) return null;
    const active = raw.filter(w => !w.removed);
    const defaultW = active.find(w => w.isDefault);
    return defaultW?.uid || active[0]?.uid || null;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(
    async ({ form, setLoading }) => {
      const values = form.getFieldsValue(true);
      const movementType = values.movementType || 'entry';
      const quantity = values.quantity || 0;

      // Validate negative stock
      if (!allowNegativeStock) {
        if (isEdit && isLastMovement) {
          // Edit mode: calculate delta between new and old signed quantities
          const oldSignedQty = editingMovement.quantity || 0; // e.g. -17
          const newSignedQty = movementType === 'entry' ? quantity : -quantity; // e.g. -10
          const qtyDelta = newSignedQty - oldSignedQty; // e.g. -10 - (-17) = +7
          const projectedStock = stockQuantity + qtyDelta;
          if (projectedStock < 0) {
            appService.message({ flag: 'w', text: 'negative_stock_not_allowed' });
            return false;
          }
        } else if (!isEdit) {
          // New movement: simple check
          const isExit = movementType === 'exit';
          if (isExit && quantity > stockQuantity) {
            appService.message({ flag: 'w', text: 'negative_stock_not_allowed' });
            return false;
          }
        }
      }

      setLoading(true);

      // Resolve warehouse
      const raw = appService.getModulePreferences({
        moduleId: 'products',
        category: 'warehouses',
      });
      const warehouses = Array.isArray(raw) ? raw : [];
      const selectedW = warehouses.find(w => w.uid === values.stockUid);

      try {
        if (isEdit) {
          // Update existing movement
          const url = API.STOCK_MOVEMENTS.MANUAL_UPDATE.replace(':_id', editingMovement._id);
          const data = { description: values.description || '' };

          if (isLastMovement) {
            data.movementType = movementType;
            data.quantity = quantity;
            data.stockUid = values.stockUid || null;
            data.stockCode = selectedW?.code || null;
            data.stockName = selectedW?.name || null;
          }

          await axiosService.put({ url, data });
        } else {
          // Create new movement
          await axiosService.post({
            url: API.STOCK_MOVEMENTS.MANUAL,
            data: {
              productId,
              movementType,
              quantity,
              description: values.description || '',
              stockUid: values.stockUid || null,
              stockCode: selectedW?.code || null,
              stockName: selectedW?.name || null,
            },
          });
        }

        appService.message({ flag: 's', text: 'saved_successfully' });
        onSuccess?.();
      } catch (err) {
        setLoading(false);
        return false;
      }
    },
    [productId, onSuccess, allowNegativeStock, stockQuantity, isEdit, editingMovement, isLastMovement],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = [step1fn({ isEdit, isLastMovement })];

  const initialValues = isEdit
    ? {
        movementType: editingMovement.movementType || 'entry',
        quantity: Math.abs(editingMovement.quantity || 0),
        description: editingMovement.notes || '',
        stockUid: editingMovement.stockUid || defaultWarehouseUid,
      }
    : {
        movementType: 'entry',
        quantity: 1,
        description: '',
        stockUid: defaultWarehouseUid,
      };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<SwapOutlined />}
      title={isEdit ? translateX('edit_movement') : translateX('new_stock_movement')}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={520}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default StockMovementModal;
